/**
 * 任意テキスト放送「放送文面 (通常テキスト)」用の、登録済みテキストの選択 UI。
 *
 * 一覧には 2 種類のテンプレートを並べ、1 件だけ選べる。
 *   - 独自テンプレート (FreeTextTemplateMaster): 選ぶと本文を放送文面の欄へ **コピー** する。
 *   - 配布テンプレート (SyncedTextMaster): 選ぶと本文はコピーせず **参照** (`synced_ref`) だけを保存する。
 *     マスタを編集すると放送地点を編集しなくても放送へ反映される。配布テンプレートは日本語専用なので、
 *     `syncedAvailable` が false (外国語を含む) のときは表を出さない。
 *
 * - 「登録済みテキストから選択」ボタンを押すと、任意テキスト画面と同じ形式の一覧モーダルを開く。
 *   一番左の「選択」列のラジオボタンで **1 件だけ** 選べ、フッターの「選択」で確定する。
 *   (行のどこをクリックしても選べる。ダブルクリックでの確定は誤操作防止のためしない)
 * - 一覧は「全線区共通」→「親の放送地点の線区」の順。本文は改行を保って全文表示する。
 *   タイトルと備考 (URL はリンク) も表示する。管理用の情報で、選択しても放送文面には入らない。
 *   編集 / 削除はここではできない (保守は任意テキスト画面で行う)。
 * - 放送文面が **空** のときは、そのまま流し込んでモーダルを閉じる。
 * - 放送文面に **すでに入力がある** ときは、上書きの確認ダイアログ (`ConfirmDialog`) を出し、
 *   OK のときだけ反映して閉じる。キャンセルでは一覧に戻る。
 *   (独自テンプレートは「選択した文面と同じ」なら確認しない。配布テンプレートは本文が捨てられるので常に確認する)
 * - 選択状態は開くたびにリセットする (選択は「操作」であり状態ではない)。
 * - 候補が 1 件も無い (未登録 / 取得失敗) ときは何も描画しない。
 *
 * モーダルは `createPortal` で `document.body` 直下に出す。放送地点フォームは `<form>` の中にあり、
 * ラジオボタン上での Enter で親フォームが送信 (保存) されるのを防ぐため。
 *
 * 上書き確認ダイアログ (`ConfirmDialog`) も同じ理由で `createPortal` で `document.body` 直下に出す。
 * 放送地点フォームの `.modal-backdrop` (position: fixed + z-index) の内側に置くと、
 * そこが独立した重なり順の単位になり、`ConfirmDialog` の z-index (1300) がフォームの外では
 * 効かない。body 直下に出た一覧モーダル (z-index 1000) の背面に回り、OK を押せなくなる。
 *
 * ⚠️ 流し込むのは本文のコピーだけ。放送生成側はテンプレートマスタを参照しないので、
 *    マスタを後から編集・削除しても登録済みの放送文面は変わらない。
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import type { FreeTextTemplate, SyncedTextRef } from '../../../api/types';
import {
  freeTextTemplateRouteLabel,
  groupFreeTextTemplatesForRoute,
  TEMPLATE_TITLE_UNSET_LABEL,
} from '../../../data/freeTextTemplates';
import type { RouteMaster } from '../../../data/routes';
import { useFreeTextTemplates } from '../../../contexts/FreeTextTemplateContext';
import { useSyncedTexts } from '../../../contexts/SyncedTextContext';
import { ConfirmDialog } from '../../ConfirmDialog';
import { LinkifiedText } from '../../LinkifiedText';

export interface FreeTextTemplatePickerProps {
  /** 放送文面欄の現在の値 (上書き確認の要否の判定に使う) */
  currentText: string;
  /** 一覧に出す線区 (親の放送地点の route_id)。未指定なら全線区共通のみ */
  routeId?: string;
  /** 線区名の表示用 */
  routes: readonly RouteMaster[];
  /** 独自テンプレートを選んだとき、その本文を放送文面欄へ反映する */
  onApply: (text: string) => void;
  /** 現在参照中の配布テンプレート (あれば、開いたときの初期選択にする) */
  syncedRef?: SyncedTextRef;
  /**
   * 配布テンプレートを選べるか (日本語のみの放送か)。false なら配布テンプレートの表は選べない。
   * 未指定は選べる。
   */
  syncedAvailable?: boolean;
  /** 配布テンプレートを選んだとき、その参照を反映する */
  onApplySynced?: (ref: SyncedTextRef) => void;
}

/** 上書き確認ダイアログの文言 (独自テンプレート) */
export const FREE_TEXT_TEMPLATE_OVERWRITE_MESSAGE =
  '入力済みの文面を上書きしますが、よろしいですか？';

/** 上書き確認ダイアログの文言 (配布テンプレート: 入力済みの本文は参照に置き換わり、保存されなくなる) */
export const SYNCED_TEXT_OVERWRITE_MESSAGE =
  '配布テンプレートを参照すると、入力済みの文面は破棄されます。よろしいですか？';

/** ラジオボタンのグループ名 (2 つの表をまたいで 1 件だけ選べるようにする) */
const RADIO_GROUP_NAME = 'free-text-template-picker';

type Kind = 'free' | 'synced';

/** 選択中の行 (種別 + 線区 + ID)。2 つのマスタで ID が衝突しても区別できるようにする */
function rowKey(kind: Kind, template: { route_id: string; template_id: string }): string {
  return `${kind}:${template.route_id}/${template.template_id}`;
}

interface PendingApply {
  kind: Kind;
  template: FreeTextTemplate;
  message: string;
}

export function FreeTextTemplatePicker({
  currentText,
  routeId,
  routes,
  onApply,
  syncedRef,
  syncedAvailable = true,
  onApplySynced,
}: FreeTextTemplatePickerProps): React.JSX.Element | null {
  const templates = useFreeTextTemplates();
  const syncedTexts = useSyncedTexts();
  /** 一覧モーダルを開いているか */
  const [open, setOpen] = useState(false);
  /** 一覧で選んでいる行 (`rowKey`)。null = 未選択 */
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** 上書き確認待ちの選択 (null = 確認中ではない) */
  const [pending, setPending] = useState<PendingApply | null>(null);

  /** 全線区共通 → 親の線区の順に並べた一覧 */
  const freeRows = useMemo(
    () => groupFreeTextTemplatesForRoute(templates, routeId).flatMap((g) => g.templates),
    [templates, routeId],
  );
  const syncedRows = useMemo(
    () => groupFreeTextTemplatesForRoute(syncedTexts, routeId).flatMap((g) => g.templates),
    [syncedTexts, routeId],
  );

  if (freeRows.length === 0 && syncedRows.length === 0) return null;

  const selectedFree = freeRows.find((t) => rowKey('free', t) === selectedKey) ?? null;
  const selectedSynced = syncedRows.find((t) => rowKey('synced', t) === selectedKey) ?? null;
  const selected = selectedFree ?? selectedSynced;

  const openModal = (): void => {
    // すでに配布テンプレートを参照していれば、その行を初期選択にする (変更前の状態が分かる)。
    setSelectedKey(syncedRef ? rowKey('synced', syncedRef) : null);
    setPending(null);
    setOpen(true);
  };

  const closeModal = (): void => {
    setOpen(false);
    setSelectedKey(null);
    setPending(null);
  };

  const apply = (kind: Kind, template: FreeTextTemplate): void => {
    if (kind === 'synced') {
      onApplySynced?.({ route_id: template.route_id, template_id: template.template_id });
    } else {
      onApply(template.text);
    }
    closeModal();
  };

  const handleConfirmSelection = (): void => {
    if (selectedFree) {
      // 入力済み (空白以外がある) で、選んだ文面と異なるときだけ確認する。
      // 同じ文面を選び直しても内容は変わらないため、確認は不要。
      if (currentText.trim() !== '' && currentText !== selectedFree.text) {
        setPending({
          kind: 'free',
          template: selectedFree,
          message: FREE_TEXT_TEMPLATE_OVERWRITE_MESSAGE,
        });
        return;
      }
      apply('free', selectedFree);
      return;
    }
    if (selectedSynced) {
      // 配布テンプレートは本文をコピーせず参照だけを保存するので、入力済みの本文は失われる。
      if (currentText.trim() !== '') {
        setPending({
          kind: 'synced',
          template: selectedSynced,
          message: SYNCED_TEXT_OVERWRITE_MESSAGE,
        });
        return;
      }
      apply('synced', selectedSynced);
    }
  };

  const renderTable = (
    kind: Kind,
    rows: readonly FreeTextTemplate[],
    testIdPrefix: string,
    ariaPrefix: string,
  ): React.JSX.Element => (
    <div className="free-text-template-picker-list">
      <table
        className="points-table free-text-template-picker-table"
        data-testid={`${testIdPrefix}-table`}
      >
        <colgroup>
          <col className="free-text-template-picker-col-select" />
          <col className="free-text-template-picker-col-route" />
          <col className="free-text-template-picker-col-title" />
          <col className="free-text-template-picker-col-text" />
        </colgroup>
        <thead>
          <tr>
            <th className="select-cell">選択</th>
            <th>線区</th>
            <th>タイトル</th>
            <th>テキスト</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((template) => {
            const key = rowKey(kind, template);
            const checked = key === selectedKey;
            return (
              <tr
                key={key}
                className={checked ? 'free-text-template-picker-row--selected' : undefined}
                data-testid={`${testIdPrefix}-row-${template.template_id}`}
                onClick={() => setSelectedKey(key)}
              >
                <td className="select-cell">
                  <input
                    type="radio"
                    name={RADIO_GROUP_NAME}
                    checked={checked}
                    aria-label={`${ariaPrefix}: ${template.title || template.text}`}
                    data-testid={`${testIdPrefix}-radio-${template.template_id}`}
                    onChange={() => setSelectedKey(key)}
                  />
                </td>
                <td>{freeTextTemplateRouteLabel(template.route_id, routes)}</td>
                <td className="free-text-template-title-cell">
                  {template.title ? (
                    <strong>{template.title}</strong>
                  ) : (
                    <span className="free-text-template-title-unset">
                      {TEMPLATE_TITLE_UNSET_LABEL}
                    </span>
                  )}
                  {template.note ? (
                    <LinkifiedText text={template.note} className="free-text-template-note" />
                  ) : null}
                </td>
                <td className="free-text-template-text-cell">{template.text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const modal = open ? (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="登録済みテキストから選択"
      data-testid="content-free-text-template-modal"
      // 背景クリック / ESC では閉じない (onClick 未設定)
    >
      <div className="modal modal--single-column free-text-template-picker-modal">
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>登録済みテキストから選択</h2>
          <button
            type="button"
            onClick={closeModal}
            aria-label="閉じる"
            data-testid="content-free-text-template-modal-close"
          >
            ×
          </button>
        </div>

        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            <p className="hint" style={{ margin: 0 }}>
              使うテキストを 1 件選んで「選択」を押してください。
            </p>

            <section data-testid="content-free-text-template-section">
              <h3 className="free-text-template-picker-heading">独自テンプレート</h3>
              <p className="hint" style={{ margin: '0 0 4px' }}>
                選ぶと、文面が「放送文面」欄にコピーされます。コピーした後は、自由に書き換えられます。
              </p>
              {freeRows.length === 0 ? (
                <p className="empty-state" data-testid="content-free-text-template-empty">
                  登録済みの独自テンプレートはありません。
                </p>
              ) : (
                renderTable(
                  'free',
                  freeRows,
                  'content-free-text-template',
                  'この独自テンプレートを選択',
                )
              )}
            </section>

            <section data-testid="content-synced-text-section">
              <h3 className="free-text-template-picker-heading">配布テンプレート</h3>
              <p className="hint" style={{ margin: '0 0 4px' }}>
                選ぶと、文面をコピーせず参照します。ひな形側を直すと、この放送地点を編集しなくても放送が約
                1 分以内に自動で変わります。そのため「放送文面」欄は編集できなくなります。
              </p>
              {!syncedAvailable ? (
                <p className="empty-state" data-testid="content-synced-text-unavailable">
                  配布テンプレートは日本語専用です。放送言語を「日本語のみ」にすると選べます。
                </p>
              ) : syncedRows.length === 0 ? (
                <p className="empty-state" data-testid="content-synced-text-empty">
                  登録済みの配布テンプレートはありません。
                </p>
              ) : (
                renderTable(
                  'synced',
                  syncedRows,
                  'content-synced-text',
                  'この配布テンプレートを選択',
                )
              )}
            </section>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={closeModal}
            data-testid="content-free-text-template-cancel"
          >
            キャンセル
          </button>
          <button
            type="button"
            className="primary"
            disabled={!selected}
            onClick={handleConfirmSelection}
            data-testid="content-free-text-template-apply"
          >
            選択
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="free-text-template-picker">
        <button type="button" data-testid="content-free-text-template-open" onClick={openModal}>
          登録済みテキストから選択
        </button>
        <small>
          独自テンプレートは文面をコピーして使い、配布テンプレートは同じ文面を参照して使います。
        </small>
      </div>

      {modal ? createPortal(modal, document.body) : null}

      {/* 一覧モーダルの後ろに回らないよう、同じ body 直下に出す (理由はファイル先頭を参照) */}
      {createPortal(
        <ConfirmDialog
          open={pending !== null}
          title="上書きの確認"
          message={pending?.message ?? FREE_TEXT_TEMPLATE_OVERWRITE_MESSAGE}
          confirmLabel="上書き"
          destructive
          onConfirm={() => {
            if (pending) apply(pending.kind, pending.template);
            else closeModal();
          }}
          onCancel={() => setPending(null)}
        />,
        document.body,
      )}
    </>
  );
}
